import { useState } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';

export default function CustomPallets() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    dimensions: '',
    quantity: '',
    material: 'hardwood',
    loadCapacity: '',
    specialRequirements: '',
    deliveryLocation: ''
  });

  const handleScroll = () => {
    setIsScrolled(window.scrollY > 50);
  };

  useState(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formData = new FormData(e.currentTarget);
      
      await fetch('https://readdy.ai/api/form/d5re50otu5df1co4dhi0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      setSubmitStatus('success');
      (e.target as HTMLFormElement).reset();
      
      setTimeout(() => {
        setSubmitStatus('idle');
      }, 5000);
    } catch (error) {
      setSubmitStatus('error');
      setTimeout(() => {
        setSubmitStatus('idle');
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation isScrolled={isScrolled} />

      {/* Hero Section */}
      <section className="relative pt-20 pb-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/367edee5da2e1dbb73f0cd7fc661f83e.jpg"
            alt="Custom Pallets Manufacturing"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-24">
          <div className="text-center text-white">
            <h1 className="text-5xl lg:text-6xl font-bold mb-6">
              Buy Custom Pallets Nationwide
            </h1>
            <p className="text-xl lg:text-2xl mb-8 max-w-3xl mx-auto">
              Tailored pallet solutions designed to meet your exact specifications. Any size, any material, delivered anywhere in the USA.
            </p>
            <a
              href="#quote-form"
              className="inline-block bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 rounded-full font-semibold text-lg transition-all cursor-pointer whitespace-nowrap"
            >
              Request Custom Quote
            </a>
          </div>
        </div>
      </section>

      {/* Why Custom Pallets Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Choose Custom Pallets?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              When standard pallets don't fit your needs, custom pallets provide the perfect solution for unique products, specialized equipment, and specific operational requirements.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-ruler-line text-2xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Exact Dimensions
              </h3>
              <p className="text-gray-600">
                Build pallets to your precise measurements, ensuring perfect fit for your products and maximizing space efficiency in storage and shipping.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-weight-line text-2xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Custom Load Capacity
              </h3>
              <p className="text-gray-600">
                Design pallets with specific weight-bearing capabilities, from lightweight applications to heavy-duty industrial loads up to 5,000+ lbs.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-leaf-line text-2xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Material Selection
              </h3>
              <p className="text-gray-600">
                Choose from hardwood, softwood, heat-treated lumber, or specialized materials to meet industry regulations and performance needs.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-tools-line text-2xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Special Features
              </h3>
              <p className="text-gray-600">
                Add custom features like reinforced corners, notched stringers, chamfered edges, or specialized deck board spacing for unique requirements.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-2xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Compliance Ready
              </h3>
              <p className="text-gray-600">
                Meet ISPM-15 international shipping standards, FDA requirements, or other industry-specific regulations with certified custom pallets.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-truck-line text-2xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Nationwide Delivery
              </h3>
              <p className="text-gray-600">
                Fast, reliable delivery to any location across the United States, with flexible scheduling to meet your project timelines.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Custom Options Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Customization Options
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Every aspect of your pallet can be customized to match your specific operational needs and product requirements.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Size & Dimensions</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-orange-600 text-xl mr-3 mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Length & Width:</strong>
                    <span className="text-gray-600"> Any dimensions from 24" to 96" to fit your products perfectly</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-orange-600 text-xl mr-3 mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Height:</strong>
                    <span className="text-gray-600"> Custom deck heights from 4" to 12" for optimal clearance</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-orange-600 text-xl mr-3 mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Overhang:</strong>
                    <span className="text-gray-600"> Specify exact overhang measurements for forklift compatibility</span>
                  </div>
                </li>
              </ul>

              <h3 className="text-2xl font-bold text-gray-900 mb-6 mt-10">Construction Style</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-orange-600 text-xl mr-3 mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Stringer Pallets:</strong>
                    <span className="text-gray-600"> 2-way or 4-way entry with notched or solid stringers</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-orange-600 text-xl mr-3 mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Block Pallets:</strong>
                    <span className="text-gray-600"> 4-way entry with blocks for maximum stability</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-orange-600 text-xl mr-3 mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Double-Face:</strong>
                    <span className="text-gray-600"> Reversible design with deck boards on both sides</span>
                  </div>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Materials & Treatment</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-orange-600 text-xl mr-3 mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Hardwood:</strong>
                    <span className="text-gray-600"> Oak, maple, or mixed hardwoods for maximum durability</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-orange-600 text-xl mr-3 mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Softwood:</strong>
                    <span className="text-gray-600"> Pine or spruce for lighter-weight applications</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-orange-600 text-xl mr-3 mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Heat Treatment:</strong>
                    <span className="text-gray-600"> ISPM-15 certified for international shipping compliance</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-orange-600 text-xl mr-3 mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Kiln-Dried:</strong>
                    <span className="text-gray-600"> Reduced moisture content for clean room environments</span>
                  </div>
                </li>
              </ul>

              <h3 className="text-2xl font-bold text-gray-900 mb-6 mt-10">Special Features</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-orange-600 text-xl mr-3 mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Reinforcement:</strong>
                    <span className="text-gray-600"> Extra stringers, blocks, or metal brackets for heavy loads</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-orange-600 text-xl mr-3 mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Deck Board Spacing:</strong>
                    <span className="text-gray-600"> Custom gaps or solid deck for specific product needs</span>
                  </div>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-orange-600 text-xl mr-3 mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Branding:</strong>
                    <span className="text-gray-600"> Company logos, stamps, or color coding available</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Industries Served Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Industries We Serve
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our custom pallets are trusted by businesses across diverse industries for their unique material handling needs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-building-line text-3xl text-orange-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Manufacturing</h3>
              <p className="text-gray-600 text-sm">Custom pallets for production lines and assembly operations</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-store-line text-3xl text-orange-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Retail & Distribution</h3>
              <p className="text-gray-600 text-sm">Optimized pallets for warehouse and retail environments</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-restaurant-line text-3xl text-orange-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Food & Beverage</h3>
              <p className="text-gray-600 text-sm">FDA-compliant pallets for food processing and storage</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-medicine-bottle-line text-3xl text-orange-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Pharmaceutical</h3>
              <p className="text-gray-600 text-sm">Clean, certified pallets for medical and pharmaceutical products</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-car-line text-3xl text-orange-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Automotive</h3>
              <p className="text-gray-600 text-sm">Heavy-duty pallets for parts and equipment transport</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-hammer-line text-3xl text-orange-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Construction</h3>
              <p className="text-gray-600 text-sm">Rugged pallets for building materials and equipment</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-ship-line text-3xl text-orange-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Export & Shipping</h3>
              <p className="text-gray-600 text-sm">ISPM-15 certified pallets for international freight</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-flask-line text-3xl text-orange-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Chemical</h3>
              <p className="text-gray-600 text-sm">Specialized pallets for hazardous material handling</p>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Form Section */}
      <section id="quote-form" className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Request Your Custom Quote
            </h2>
            <p className="text-xl text-gray-600">
              Tell us about your requirements and we'll provide a detailed quote within 24 hours.
            </p>
          </div>

          {/* Quote Request Form */}
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 lg:p-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Request a Custom Quote</h2>
            <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form>
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-600 focus:border-transparent text-sm"
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-600 focus:border-transparent text-sm"
                    placeholder="john@company.com"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-600 focus:border-transparent text-sm"
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-semibold text-gray-900 mb-2">
                    Company Name
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-600 focus:border-transparent text-sm"
                    placeholder="Your Company Inc."
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="dimensions" className="block text-sm font-semibold text-gray-900 mb-2">
                    Desired Dimensions (L × W × H) *
                  </label>
                  <input
                    type="text"
                    id="dimensions"
                    name="dimensions"
                    value={formData.dimensions}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-600 focus:border-transparent text-sm"
                    placeholder="e.g., 42 × 36 × 6 inches"
                  />
                </div>

                <div>
                  <label htmlFor="quantity" className="block text-sm font-semibold text-gray-900 mb-2">
                    Quantity Needed *
                  </label>
                  <input
                    type="text"
                    id="quantity"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-600 focus:border-transparent text-sm"
                    placeholder="e.g., 500 pallets"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="material" className="block text-sm font-semibold text-gray-900 mb-2">
                    Preferred Material *
                  </label>
                  <select
                    id="material"
                    name="material"
                    value={formData.material}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-600 focus:border-transparent text-sm cursor-pointer"
                  >
                    <option value="hardwood">Hardwood</option>
                    <option value="softwood">Softwood</option>
                    <option value="mixed">Mixed Wood</option>
                    <option value="heat-treated">Heat-Treated (ISPM-15)</option>
                    <option value="not-sure">Not Sure / Need Recommendation</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="loadCapacity" className="block text-sm font-semibold text-gray-900 mb-2">
                    Load Capacity Required
                  </label>
                  <input
                    type="text"
                    id="loadCapacity"
                    name="loadCapacity"
                    value={formData.loadCapacity}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-600 focus:border-transparent text-sm"
                    placeholder="e.g., 2,500 lbs"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="deliveryLocation" className="block text-sm font-semibold text-gray-900 mb-2">
                  Delivery Location *
                </label>
                <input
                  type="text"
                  id="deliveryLocation"
                  name="deliveryLocation"
                  value={formData.deliveryLocation}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-600 focus:border-transparent text-sm"
                  placeholder="City, State or ZIP Code"
                />
              </div>

              <div className="mb-6">
                <label htmlFor="specialRequirements" className="block text-sm font-semibold text-gray-900 mb-2">
                  Special Requirements or Additional Details
                </label>
                <textarea
                  id="specialRequirements"
                  name="specialRequirements"
                  value={formData.specialRequirements}
                  onChange={handleChange}
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-600 focus:border-transparent text-sm resize-none"
                  placeholder="Please describe any special features, construction requirements, compliance needs, or other details about your custom pallet project..."
                ></textarea>
                <p className="text-xs text-gray-500 mt-2">Maximum 500 characters</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Quote Request'}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-center">
                  Thank you! We'll contact you within 24 hours.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-center">
                  Something went wrong. Please try again.
                </div>
              )}

              <p className="text-sm text-gray-600 text-center mt-6">
                We'll review your requirements and contact you within 24 hours with a detailed quote and recommendations.
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Choose Nationwide Pallet Solutions?
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-award-line text-4xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Expert Engineering</h3>
              <p className="text-gray-600">
                Our team works with you to design pallets that meet your exact specifications and optimize your operations.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-time-line text-4xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Fast Turnaround</h3>
              <p className="text-gray-600">
                Quick production and delivery timelines to keep your projects on schedule, with rush orders available.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-line text-4xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Quality Guaranteed</h3>
              <p className="text-gray-600">
                Every custom pallet is built to exacting standards and thoroughly inspected before delivery.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}